import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "@/api/school";

// Token Plan 配额卡 —— 仅园长/管理员可见(接口 403 时整卡隐藏)。
// 不展示任何真实 token/费用数字,只有百分比 + 重置日(设计同 OS dashboard)。
export function PlanUsageCard() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["school", "usage", "plan"],
    refetchInterval: 60_000,
    retry: false,
    queryFn: async () => {
      const r = await fetch("/api/school/usage/plan", {
        credentials: "include", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(String(r.status));
      return r.json() as Promise<{ available: boolean; error?: string;
        warn_threshold?: number; plan_name?: string; percent_used?: number;
        resets_at?: number; allowance_reached?: boolean;
        overage_enabled?: boolean; overage_used?: number; overage_cap?: number }>;
    },
  });
  const d = q.data;
  if (!d) return null;
  if (!d.available) {
    return (
      <div className="mt-4 mb-5 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">{t("planUsage.title")}</h3>
        <p className="text-xs text-amber-600">{t("planUsage.unavailable")}</p>
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, d.percent_used ?? 0));
  const warnAt = d.warn_threshold ?? 80;
  const exhausted = d.allowance_reached || pct >= 100;
  const warned = !exhausted && pct >= warnAt;
  const color = exhausted ? "#e11d48" : warned ? "#d97706" : "#059669";
  const resetDate = d.resets_at
    ? new Date(d.resets_at * 1000).toLocaleDateString()
    : "—";
  return (
    <div className="mt-4 mb-5 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-semibold text-slate-500 uppercase">{t("planUsage.title")}</h3>
        <span className="text-lg font-semibold" style={{ color }}>
          {/* 低用量显示一位小数,避免 0.1% 取整成 "0" 像坏了(2026-08-18)。 */}
          {pct > 0 && pct < 10 ? pct.toFixed(1) : pct.toFixed(0)}%
        </span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="mt-1.5 text-xs text-slate-500">{t("planUsage.resets", { date: resetDate })}</p>
      {warned && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
          {t("planUsage.warn", { pct: warnAt })}
        </p>
      )}
      {exhausted && (
        <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
          {t("planUsage.exhausted", { date: resetDate })}
        </p>
      )}
    </div>
  );
}

